import { computed } from 'vue'
import { usePlayerStore } from '@/stores/player'

export function useFormatters() {
  const player = usePlayerStore()
  const locale = computed(() => player.profile?.settings?.locale ?? navigator.language ?? 'en-GB')

  function _useMiles() {
    const tag = locale.value
    // Explicit fallback for US territories where measurementSystem may be unsupported
    if (/^en-US|^en-AS|^en-GU|^en-MP|^en-PR|^en-VI/.test(tag)) return true
    try {
      return new Intl.Locale(tag).measurementSystem === 'ussystem'
    } catch {
      return false
    }
  }

  function fmtDistance(km) {
    if (!km) return ''
    if (_useMiles()) return Math.round(km * 0.621371) + ' mi'
    return Math.round(km) + ' km'
  }

  function fmtGameDateTime(iso) {
    if (!iso) return '—'
    const d = new Date(iso + 'Z')
    const date = new Intl.DateTimeFormat(locale.value, { day: '2-digit', month: 'short', timeZone: 'UTC' }).format(d)
    const time = new Intl.DateTimeFormat(locale.value, { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }).format(d)
    return `${date} · ${time}`
  }

  function fmtGameDate(iso) {
    if (!iso) return '—'
    const d = new Date(iso + 'Z')
    return new Intl.DateTimeFormat(locale.value, { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(d)
  }

  function fmtAxisDate(ms) {
    return new Intl.DateTimeFormat(locale.value, { day: '2-digit', month: 'short', timeZone: 'UTC' }).format(new Date(ms))
  }

  function fmtDate(iso) {
    if (!iso) return '—'
    return new Intl.DateTimeFormat(locale.value, { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(iso))
  }

  function fmtTime(ts) {
    if (!ts) return '—'
    return new Intl.DateTimeFormat(locale.value, { hour: '2-digit', minute: '2-digit' }).format(new Date(ts))
  }

  function fmtMoney(n) {
    if (n == null) return '—'
    return '$' + new Intl.NumberFormat(locale.value, { maximumFractionDigits: 0 }).format(Number(n))
  }

  function fmtMoneySign(n) {
    if (n == null) return '—'
    const num = Number(n)
    const prefix = num >= 0 ? '+$' : '-$'
    return prefix + new Intl.NumberFormat(locale.value, { maximumFractionDigits: 0 }).format(Math.abs(num))
  }

  function fmtTransitDays(days) {
    if (days == null) return ''
    const n = Number(days)
    const totalHours = Math.round(n * 24)
    if (totalHours < 1) return '< 1 h'
    if (totalHours < 24) return totalHours + ' h'
    const d = Math.floor(totalHours / 24)
    const h = totalHours % 24
    return h > 0 ? `${d}d ${h}h` : `${d}d`
  }

  return { fmtDistance, fmtGameDateTime, fmtGameDate, fmtAxisDate, fmtDate, fmtTime, fmtMoney, fmtMoneySign, fmtTransitDays }
}
